import type { LayerSpecification, StyleSpecification } from 'maplibre-gl'

/**
 * The map is an ordinary basemap with our own edits drawn on top of it, out of
 * one PMTiles archive that contains nothing but those edits.
 *
 * Basemap tiles come from OpenFreeMap, which needs no API key and sends CORS
 * headers — both required for a page served from GitHub Pages.
 */

export const BASEMAP_STYLE = 'https://tiles.openfreemap.org/styles/positron'
export const TILES_SOURCE = 'iugnorge'

export const colours = {
  building: '#e8590c',
  buildingLine: '#b34700',
  road: '#e8590c',
  water: '#1c7ed6',
  square: '#e8590c',
  squareText: '#8a3600',
}

/** The layers we add on top of the basemap, in draw order. */
export function highlightLayers(tilesUrl: string): {
  source: StyleSpecification['sources'][string]
  layers: LayerSpecification[]
} {
  return {
    source: { type: 'vector', url: `pmtiles://${tilesUrl}`, attribution: '© OpenStreetMap contributors' },
    layers: [
      // Task squares sit underneath: they are context for the edits, not the point.
      {
        id: 'task-fill',
        type: 'fill',
        source: TILES_SOURCE,
        'source-layer': 'tasks',
        paint: {
          'fill-color': colours.square,
          // Uniform, and light. Shading by edit count was tried and read as a
          // rendering fault rather than as a scale — the first person to see it
          // asked whether the darker squares were being drawn twice. The counts
          // live on the label and on click instead, where they are unambiguous.
          'fill-opacity': 0.1,
        },
      },
      {
        id: 'task-outline',
        type: 'line',
        source: TILES_SOURCE,
        'source-layer': 'tasks',
        paint: {
          'line-color': colours.square,
          'line-opacity': 0.55,
          'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 14, 1.2, 18, 2],
        },
      },
      {
        id: 'task-label',
        type: 'symbol',
        source: TILES_SOURCE,
        'source-layer': 'tasks',
        // Only once a square fills enough of the screen to be worth reading.
        // Below this the labels outnumber the map.
        minzoom: 15,
        layout: {
          'text-field': [
            'concat',
            ['to-string', ['get', 'edits']],
            ['case', ['==', ['get', 'edits'], 1], ' edit', ' edits'],
          ],
          'text-font': ['Noto Sans Regular'],
          'text-size': 11,
          'text-allow-overlap': false,
        },
        paint: { 'text-color': colours.squareText, 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
      },

      {
        id: 'our-waterways',
        type: 'line',
        source: TILES_SOURCE,
        'source-layer': 'waterways',
        paint: {
          'line-color': colours.water,
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 1, 16, 3.5],
        },
      },
      {
        id: 'our-roads',
        type: 'line',
        source: TILES_SOURCE,
        'source-layer': 'roads',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': colours.road,
          'line-width': ['interpolate', ['exponential', 1.6], ['zoom'], 10, 1, 14, 2.5, 18, 7],
        },
      },
      {
        id: 'our-buildings',
        type: 'fill',
        source: TILES_SOURCE,
        'source-layer': 'buildings',
        paint: { 'fill-color': colours.building, 'fill-opacity': 0.85 },
      },
      {
        id: 'our-buildings-outline',
        type: 'line',
        source: TILES_SOURCE,
        'source-layer': 'buildings',
        minzoom: 15,
        paint: { 'line-color': colours.buildingLine, 'line-width': 0.6 },
      },
    ],
  }
}

export const MARKERS_SOURCE = 'projects'

/**
 * One dot per project for the globe view. The tile archive starts at z4, and
 * individual buildings are sub-pixel at world scale anyway — a dot sized by how
 * much was mapped says far more than a smudge of geometry would.
 */
export const markerLayers = (): LayerSpecification[] => [
  {
    id: 'project-dot',
    type: 'circle',
    source: MARKERS_SOURCE,
    // One level further out than the features they stand in for, so the
    // handover to the real geometry happens sooner.
    maxzoom: 7,
    paint: {
      'circle-color': colours.building,
      'circle-opacity': 0.8,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 1.2,
      // Area, not radius, tracks the count — otherwise the big projects swamp
      // everything else.
      'circle-radius': ['interpolate', ['linear'], ['sqrt', ['get', 'features']], 0, 3.5, 25, 9, 71, 20],
    },
  },
  {
    id: 'project-dot-label',
    type: 'symbol',
    source: MARKERS_SOURCE,
    minzoom: 2,
    maxzoom: 7,
    layout: {
      'text-field': ['get', 'label'],
      'text-font': ['Noto Sans Regular'],
      'text-size': 11,
      'text-offset': [0, 1.1],
      'text-anchor': 'top',
    },
    paint: { 'text-color': colours.squareText, 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
  },
]

/** Layer ids that respond to a click, most specific first. */
export const CLICKABLE = ['project-dot', 'our-buildings', 'task-fill']
